import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import type {
  EnrichedMessage,
  EnrichedMessagePage,
} from "@/payroll/editor/types";
import { OwnerMessage } from "./owner-message";
import { TranscriptMessage } from "./transcript-message";
import { TranscriptSkeleton } from "./transcript-skeleton";
import { TurnActivity } from "./turn-activity";
import type { PostedOutgoing } from "./use-outgoing";

/**
 * How the transcript meets the pane's edges.
 *
 * It fades to `gray-10`, the ground the conversation actually sits on. It used
 * to fade to white, which was right when this pane was white and became a pale
 * haze over the text the moment the editor's chrome went grey: the words did
 * not dissolve into anything, they just got foggy.
 *
 * Gentler than before, too. A short fade to the exact ground colour reads as
 * the text running under an edge; a tall strong one reads as a smear.
 */
const FADE =
  "pointer-events-none absolute inset-x-0 z-10 h-10 from-gray-10 to-transparent";

// How much has to be below the fold before the bottom fade means anything.
// Fractional layout leaves a pixel of slack at the true bottom, so a stricter
// test leaves the fade lit over the last line with nothing under it.
const EDGE = 2;
// Close enough to the foot that new content should carry the reader with it.
const FOLLOW = 80;

/** The owner's own message nearest above this one. */
const lastRequestBefore = (messages: EnrichedMessage[], index: number) => {
  for (let at = index; at >= 0; at -= 1) {
    const message = messages[at];
    if (message?.role === "user") return message.content;
  }
  return "";
};

export const EditorTranscript = ({
  page,
  loading,
  working,
  isSending,
  onReply,
  onWriteOwn,
  onResend,
  canResend,
  posted,
  onRetry,
  onDiscard,
  queueCount,
}: {
  page?: EnrichedMessagePage;
  /** Nothing is known yet. Distinct from a conversation with no messages. */
  loading: boolean;
  /**
   * A request was already running when this page opened. Known from the
   * workspace, which answers before the transcript does.
   */
  working: boolean;
  isSending: boolean;
  onReply: (value: string) => void;
  onWriteOwn: () => void;
  onResend: (request: string) => void;
  /** Sending is possible at all: not that this message is the newest one. */
  canResend: boolean;
  /** Sent, not yet in the transcript. Rendered as ordinary messages. */
  posted: PostedOutgoing[];
  onRetry: (id: string) => void;
  onDiscard: (id: string) => void;
  /** Queue rows grow the composer, which shortens this pane under the reader. */
  queueCount: number;
}) => {
  const scroll = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const nearBottom = useRef(true);
  // Whether anything has scrolled under the pane's top edge yet. The fade only
  // has something to do once it has.
  const [scrolled, setScrolled] = useState(false);
  // And whether anything is still below the bottom edge. The same fade, the
  // other way up: without it the last line runs into the composer with nothing
  // to say the transcript continues under it.
  const [more, setMore] = useState(false);
  // Whatever the server is working on now. The steps ride on the user message
  // that started the turn, which is also the one the progress line describes.
  const steps = page?.latest_request?.steps ?? [];
  const messages = page?.messages ?? [];
  // One place that reads the viewport. It was written out three times, once per
  // caller, and they had already drifted apart by a threshold.
  const sync = useCallback(() => {
    const node = scroll.current;
    if (!node) return;
    const remaining = node.scrollHeight - node.scrollTop - node.clientHeight;
    nearBottom.current = remaining < FOLLOW;
    setScrolled(node.scrollTop > 0);
    setMore(remaining > EDGE);
  }, []);
  // `scroll` does not bubble, so the listener goes on the viewport itself.
  //
  // It depends on `loading` because of where the ref comes from. While the
  // skeleton is up this component returns before the ScrollArea, so there is no
  // viewport and no ref. With an empty dependency list the effect ran once
  // against that null, bailed, and never ran again: the listener was never
  // attached for the whole life of the pane and neither edge fade ever
  // appeared, however far the reader scrolled.
  useEffect(() => {
    const node = scroll.current;
    if (!node) return;
    sync();
    node.addEventListener("scroll", sync);
    return () => node.removeEventListener("scroll", sync);
  }, [loading, sync]);
  // The transcript grows while a turn runs, a step at a time, and none of that
  // touches the props below. A reader sitting at the foot was left behind by
  // every new line: the content got taller under them, so the bottom fade lit
  // over text that had just appeared and nothing scrolled to show it. Watching
  // the content itself is the only thing that sees those growths.
  useEffect(() => {
    const node = content.current;
    const viewport = scroll.current;
    if (!node || !viewport) return;
    const observer = new ResizeObserver(() => {
      if (nearBottom.current) viewport.scrollTop = viewport.scrollHeight;
      sync();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, sync]);
  useEffect(() => {
    if (isSending) nearBottom.current = true;
    if (nearBottom.current && scroll.current)
      scroll.current.scrollTop = scroll.current.scrollHeight;
    // Sending auto-scrolls, which is a scroll the listener never sees.
    sync();
  }, [messages.length, isSending, posted.length, queueCount, sync]);
  if (loading) return <TranscriptSkeleton working={working} />;
  return (
    <div className="relative min-h-0 flex-1">
      {/* The transcript scrolls under the pane's top edge with nothing to mark
          it, so a line of text sits half-cut at the boundary. Text dissolves
          into the pane instead: a wash of the surface colour carries most of
          it, a small blur softens the glyph edges so the fade does not look
          like flat opacity, and one mask retires both within the strip's own
          height.

          The line at the very top stays readable: the wash stops short of the
          surface colour and the mask never holds, so that line recedes rather
          than disappearing. Full opacity erased it and read as a bar across the
          pane. Strength and height were picked together against a rendered
          ladder — 80% over 64px lands where 80% over 48px is abrupt and 60%
          over 48px is invisible.

          It renders only once something has actually scrolled under the edge.
          Sitting there permanently, it softened the first line of a transcript
          that had not moved at all, which on a short transcript is the only
          text on screen. */}
      {scrolled && (
        <div aria-hidden className={cn(FADE, "top-0 bg-gradient-to-b")} />
      )}
      {/* The same treatment at the foot of the pane, so the transcript fades
          under the composer rather than being cut off by it. */}
      {more && (
        <div aria-hidden className={cn(FADE, "bottom-0 bg-gradient-to-t")} />
      )}
      <ScrollArea vertical viewportRef={scroll} className="h-full">
        <Stack
          gap="none"
          align="stretch"
          direction="column"
          ref={content}
          className="flex flex-col gap-4 px-5 pb-3 pt-5"
        >
          {/* The invitation, only once there is nothing to invite onto. It
              used to render whenever the list was empty, which includes every
              reload before the first answer comes back, so a thread with five
              messages opened by asking what should change. */}
          {!messages.length && !loading && (
            <p className="text-secondary-text">
              What should change? Describe the rule and which employees it
              affects.
            </p>
          )}
          {messages.map((message, index) => (
            <TranscriptMessage
              key={message.id}
              message={message}
              disabled={message.request_id !== page?.latest_request?.id}
              onReply={onReply}
              onWriteOwn={onWriteOwn}
              onResend={onResend}
              canResend={canResend}
              // The words that asked for this. An answer that failed can then
              // be sent again without the owner retyping what they said.
              request={lastRequestBefore(messages, index)}
            />
          ))}
          {/* Sent, and in the conversation where it belongs. Nothing marks the
              ordinary case: a message on its way looks like a message. Only a
              failure speaks up, on the bubble itself, with the two things worth
              offering — try again, or take the words back. */}
          {posted.map((item) => (
            // The same wrapper a delivered message gets, so the bubble's parent
            // chain is identical and nothing shifts when the real one arrives.
            <div key={item.id} className="min-w-0 break-words">
              {/* A message the server saved is already in the list above. Only
                  its failure row is missing, so drawing the bubble again would
                  show the reader their words twice. */}
              {!item.saved && <OwnerMessage>{item.message}</OwnerMessage>}
              {/* Try again, or throw it away. Edit used to be here too, and it
                  worked by pulling the words back into the composer, which
                  threw away whatever was already typed there. It could not do
                  what it looked like it did either: a saved request keeps its
                  id, and the server refuses that id carrying different words.
                  Discard then write is the honest version of the same move. */}
              {item.failed && (
                <Stack
                  gap="none"
                  align="stretch"
                  direction="row"
                  role="alert"
                  className="ml-auto mt-1 flex items-center gap-2 text-sm"
                >
                  <span>
                    {item.saved ? "Saved, but it did not start." : "Not sent."}
                  </span>
                  <Button
                    size="xs"
                    variant="text"
                    className="h-auto p-0"
                    onClick={() => onRetry(item.id)}
                  >
                    Try again
                  </Button>
                  <Button
                    size="xs"
                    variant="text"
                    className="h-auto p-0"
                    onClick={() => onDiscard(item.id)}
                  >
                    Discard
                  </Button>
                </Stack>
              )}
            </div>
          ))}
          {/* Something of the reader's is in hand. `isActive` is the server
              saying it is working; `isSending` covers the gap before that is
              true — the request on its way out, and the wait for a stopped
              request to let go after Send now. Without the second case the
              screen said nothing at all between the two, which reads as a
              message that went nowhere. */}
          {(page?.isActive || isSending) && (
            <Stack
              gap="none"
              align="stretch"
              direction="column"
              role="status"
              className="flex flex-col gap-1"
            >
              {/* The steps so far, then where it is now. A long turn spends
                  minutes on one stage, so the sequence behind it is what
                  shows the request is moving rather than stuck. */}
              {!!steps.length && <TurnActivity steps={steps} />}
              {!steps.length && (
                <p className="flex items-center gap-2 text-secondary-text">
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 animate-pulse rounded-full bg-stroke-interactive"
                  />
                  {page?.isActive ? page.progressLabel : "Sending…"}
                </p>
              )}
            </Stack>
          )}
        </Stack>
      </ScrollArea>
    </div>
  );
};
